import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import authService from "../../services/authService";
import { Oval } from 'react-loader-spinner';
import moment from 'moment';

export default function Commissions() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [commissions, setCommissions] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [activeTab, setActiveTab] = useState('commissions');
    const [stats, setStats] = useState(null);
    const [selectedCommission, setSelectedCommission] = useState(null);
    const [showCommissionModal, setShowCommissionModal] = useState(false);
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawalData, setWithdrawalData] = useState({
        amount: 0,
        bank_name: '',
        account_number: '',
        account_holder_name: ''
    });
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const [withdrawError, setWithdrawError] = useState('');

    useEffect(() => {
        // Check if user is logged in
        const checkAuth = async () => {
            try {
                if (!authService.isLoggedIn()) {
                    navigate('/auth-login');
                    return;
                }

                // Get current user data
                const currentUser = authService.getCurrentUser();
                setUser(currentUser);

                // Fetch commission stats
                try {
                    const commissionStatsResponse = await authService.getCommissionStats();
                    setStats(commissionStatsResponse);
                } catch (err) {
                    console.error("Error fetching commission stats:", err);
                }

                // Fetch commissions
                try {
                    const commissionsResponse = await authService.getCommissions();
                    setCommissions(commissionsResponse.data || []);
                } catch (err) {
                    console.error("Error fetching commissions:", err);
                }

                // Fetch withdrawals
                try {
                    const withdrawalsResponse = await authService.getWithdrawals();
                    console.log("Withdrawals response:", withdrawalsResponse); // Debug
                    setWithdrawals(withdrawalsResponse.data || withdrawalsResponse.withdrawals || []);
                } catch (err) {
                    console.error("Error fetching withdrawals:", err);
                }

                setLoading(false);
            } catch (err) {
                console.error("Authentication error:", err);
                navigate('/auth-login');
            }
        };

        checkAuth();
    }, [navigate]);

    useEffect(() => {
        // Pre-fill withdrawal form with user data if available
        if (user) {
            setWithdrawalData(prev => ({
                ...prev,
                bank_name: user.bank_name || '',
                account_number: user.bank_account_number || '',
                account_holder_name: user.bank_account_name || ''
            }));
        }
    }, [user]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(amount);
    };

    const getStatusBadgeClass = (status) => {
        switch (status.toLowerCase()) {
            case 'pending':
                return 'bg-warning';
            case 'completed':
            case 'paid':
            case 'approved':
                return 'bg-success';
            case 'processing':
                return 'bg-info';
            case 'rejected':
            case 'failed':
            case 'cancelled':
                return 'bg-danger';
            default:
                return 'bg-secondary';
        }
    };

    const viewCommissionDetails = async (commission) => {
        try {
            if (commission.id) {
                const details = await authService.getCommissionDetails(commission.id);
                console.log("Commission details:", details);
                
                // Check if we need to fetch additional booking details
                if (details.booking && details.booking.id && (!details.booking.customer_info || !details.booking.attraction)) {
                    try {
                        // Fetch complete booking details if not already included
                        const bookingDetails = await authService.getBookingDetails(details.booking.id);
                        if (bookingDetails) {
                            details.booking = {
                                ...details.booking,
                                ...bookingDetails.booking,
                                customer_info: bookingDetails.booking.customer_info || details.booking.customer_info
                            };
                        }
                    } catch (err) {
                        console.error("Error fetching complete booking details:", err);
                    }
                }
                
                // Process booking customer info from paymentNotes if not available
                if (details.booking && !details.booking.customer_info && details.booking.paymentNotes) {
                    const customerInfo = {};
                    const notes = details.booking.paymentNotes;
                    
                    // Extract customer info using regex patterns
                    const nameMatch = notes.match(/Name:\s*([^,]+?)(?:,|\s*$)/i);
                    const emailMatch = notes.match(/Email:\s*([^,]+?)(?:,|\s*$)/i);
                    const phoneMatch = notes.match(/Phone:\s*([^,]+?)(?:,|\s*$)/i);
                    const groupMatch = notes.match(/Group:\s*([^,]+?)(?:,|\s*$)/i);
                    const domicileMatch = notes.match(/Domicile:\s*([^,]+?)(?:,|\s*$)/i);
                    
                    if (nameMatch && nameMatch[1]) customerInfo.name = nameMatch[1].trim();
                    if (emailMatch && emailMatch[1]) customerInfo.email = emailMatch[1].trim();
                    if (phoneMatch && phoneMatch[1]) customerInfo.phone = phoneMatch[1].trim();
                    if (groupMatch && groupMatch[1]) customerInfo.groupName = groupMatch[1].trim();
                    if (domicileMatch && domicileMatch[1]) customerInfo.domicile = domicileMatch[1].trim();
                    
                    details.booking.customer_info = customerInfo;
                }
                
                setSelectedCommission({
                    ...commission,
                    ...details,
                    booking: details.booking || {}
                });
                setShowCommissionModal(true);
            }
        } catch (error) {
            console.error("Error fetching commission details:", error);
        }
    };

    const downloadCommissionPDF = (commission) => {
        // In a real implementation, this would generate a PDF with commission details
        // For this example, we'll create a simple text version for download
        
        const bookingInfo = commission.booking || {};
        
        // Extract customer info from paymentNotes if not available in customer_info
        let customerName = 'N/A';
        let customerGroup = 'N/A';
        
        if (bookingInfo.customer_info && bookingInfo.customer_info.name) {
            customerName = bookingInfo.customer_info.name;
            customerGroup = bookingInfo.customer_info.groupName || 'N/A';
        } else if (bookingInfo.paymentNotes) {
            const nameMatch = bookingInfo.paymentNotes.match(/Name:\s*([^,]+?)(?:,|\s*$)/i);
            const groupMatch = bookingInfo.paymentNotes.match(/Group:\s*([^,]+?)(?:,|\s*$)/i);
            
            if (nameMatch && nameMatch[1]) customerName = nameMatch[1].trim();
            if (groupMatch && groupMatch[1]) customerGroup = groupMatch[1].trim();
        }
        
        // Calculate total tickets
        let totalTickets = 0;
        if (bookingInfo.tickets && bookingInfo.tickets.length > 0) {
            totalTickets = bookingInfo.tickets.reduce((sum, ticket) => sum + ticket.quantity, 0);
        }
        
        // Get attraction name
        const attractionName = bookingInfo.attraction ? bookingInfo.attraction.title : 
                             (bookingInfo.attraction_id ? `Attraction #${bookingInfo.attraction_id}` : 'N/A');
        
        const content = `
Commission Details
-----------------
ID: ${commission.id}
Date: ${moment(commission.created_at).format('DD MMM YYYY')}
Amount: ${formatCurrency(commission.amount)}
Status: ${commission.status}

Booking Information
-----------------
Booking Code: ${bookingInfo.bookingCode || 'N/A'}
Customer Name: ${customerName}
Group: ${customerGroup}
Date: ${bookingInfo.date ? moment(bookingInfo.date).format('DD MMM YYYY') : 'N/A'}
Attraction: ${attractionName}
Total Tickets: ${totalTickets}
Total Amount: ${formatCurrency(bookingInfo.grandTotal || 0)}
        `;
        
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `commission-${commission.id}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleWithdrawalSubmit = async (e) => {
        e.preventDefault();
        setWithdrawLoading(true);
        setWithdrawError('');

        try {
            // Verify minimum withdrawal amount
            if (parseFloat(withdrawalData.amount) < 100000) {
                setWithdrawError('Jumlah penarikan minimum adalah Rp 100.000');
                setWithdrawLoading(false);
                return;
            }

            // Verify available balance
            if (parseFloat(withdrawalData.amount) > parseFloat(stats.available_balance)) {
                setWithdrawError('Amount exceeds your available balance');
                setWithdrawLoading(false);
                return;
            }

            const response = await authService.requestWithdrawal(withdrawalData);
            
            // Refresh withdrawals list
            const withdrawalsResponse = await authService.getWithdrawals();
            setWithdrawals(withdrawalsResponse.withdrawals || []);
            
            // Refresh commission stats
            const commissionStatsResponse = await authService.getCommissionStats();
            setStats(commissionStatsResponse);
            
            // Close the modal and show success message
            setShowWithdrawModal(false);
            alert('Withdrawal request submitted successfully!');
            
            // Reset the form amount (keep bank details)
            setWithdrawalData(prev => ({
                ...prev,
                amount: 0
            }));
            
        } catch (error) {
            console.error("Withdrawal error:", error);
            setWithdrawError(error.message || 'Failed to process withdrawal request');
        } finally {
            setWithdrawLoading(false);
        }
    };

    const cancelWithdrawal = async (withdrawalId) => {
        if (!window.confirm('Are you sure you want to cancel this withdrawal request?')) {
            return;
        }
        
        try {
            await authService.cancelWithdrawal(withdrawalId);
            
            // Refresh withdrawals list
            const withdrawalsResponse = await authService.getWithdrawals();
            setWithdrawals(withdrawalsResponse.withdrawals || []);
            
            // Refresh commission stats
            const commissionStatsResponse = await authService.getCommissionStats();
            setStats(commissionStatsResponse);
            
            alert('Withdrawal request cancelled successfully');
        } catch (error) {
            console.error("Error cancelling withdrawal:", error);
            alert('Failed to cancel withdrawal: ' + (error.message || 'Unknown error'));
        }
    };

    if (loading) {
        return (
            <div className="min-vh-100 d-flex justify-content-center align-items-center">
                <Oval
                    visible={true}
                    height="80"
                    width="80"
                    color="#4fa94d"
                    ariaLabel="oval-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                />
                <p className="ms-3">Memuat data komisi...</p>
            </div>
        );
    }

    return (
        <>
            <Navbar navClass="defaultscroll sticky" menuClass="navigation-menu nav-left" />
            
            <section className="section mt-5 pt-4">
                <div className="container mt-100 mt-60">
                    <div className="row">
                        <div className="col-lg-4 col-md-6 col-12 d-lg-block">
                            <div className="sidebar sticky-bar p-4 rounded bg-light">
                                <div className="text-center">
                                    <h5 className="mb-0">{user?.name || "Pengguna"}</h5>
                                    <p className="text-muted mt-1 mb-0">{user?.email}</p>
                                </div>

                                <div className="mt-4">
                                    <ul className="list-unstyled sidebar-nav mb-0">
                                        <li className="navbar-item"><Link to="/user/dashboard" className="navbar-link"><i className="mdi mdi-view-dashboard me-2"></i>Dashboard</Link></li>
                                        <li className="navbar-item active"><Link to="/user/commissions" className="navbar-link"><i className="mdi mdi-currency-usd me-2"></i>Komisi</Link></li>
                                        <li className="navbar-item"><Link to="/user/profile" className="navbar-link"><i className="mdi mdi-account me-2"></i>Profil</Link></li>
                                        <li className="navbar-item">
                                            <Link to="#" className="navbar-link" onClick={() => {
                                                authService.logout();
                                                navigate('/auth-login');
                                            }}><i className="mdi mdi-logout me-2"></i>Keluar</Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-8 col-12">
                            <div className="card border-0 rounded shadow">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">Ringkasan Komisi</h5>
                                        
                                        {stats && stats.available_balance > 0 && (
                                            <button 
                                                className="btn btn-primary btn-sm"
                                                onClick={() => setShowWithdrawModal(true)}
                                            >
                                                <i className="mdi mdi-cash-multiple me-1"></i> Tarik Dana
                                            </button>
                                        )}
                                    </div>

                                    {stats && (
                                        <div className="row mb-4">
                                            <div className="col-md-4">
                                                <div className="card border-0 bg-light p-3">
                                                    <h6 className="text-muted">Saldo Tersedia</h6>
                                                    <h4>{formatCurrency(stats.available_balance || 0)}</h4>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="card border-0 bg-light p-3">
                                                    <h6 className="text-muted">Total Diterima</h6>
                                                    <h4>{formatCurrency(stats.total_earned || 0)}</h4>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="card border-0 bg-light p-3">
                                                    <h6 className="text-muted">Tertunda</h6>
                                                    <h4>{formatCurrency(stats.pending_amount || 0)}</h4>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <ul className="nav nav-tabs border-bottom">
                                                <li className="nav-item">
                                                    <a 
                                                        className={`nav-link ${activeTab === 'commissions' ? 'active' : ''}`} 
                                                        href="#" 
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setActiveTab('commissions');
                                                        }}
                                                    >
                                                        <i className="mdi mdi-currency-usd me-2"></i>Komisi Diterima
                                                    </a>
                                                </li>
                                                <li className="nav-item">
                                                    <a 
                                                        className={`nav-link ${activeTab === 'withdrawals' ? 'active' : ''}`} 
                                                        href="#" 
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setActiveTab('withdrawals');
                                                        }}
                                                    >
                                                        <i className="mdi mdi-cash-multiple me-2"></i>Riwayat Penarikan
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    {activeTab === 'commissions' && (
                                        <>
                                            <div className="table-responsive">
                                                {commissions.length > 0 ? (
                                                    <table className="table table-hover table-bordered">
                                                        <thead className="bg-light">
                                                            <tr>
                                                                <th>Tanggal</th>
                                                                <th>Referensi</th>
                                                                <th>Jumlah</th>
                                                                <th>Status</th>
                                                                <th>Aksi</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {commissions.map((commission, index) => (
                                                                <tr key={index}>
                                                                    <td>{moment(commission.created_at).format('DD MMM YYYY')}</td>
                                                                    <td>{commission.booking?.bookingCode || 'N/A'}</td>
                                                                    <td>{formatCurrency(commission.amount || 0)}</td>
                                                                    <td>
                                                                        <span className={`badge ${getStatusBadgeClass(commission.status)}`}>
                                                                            {commission.status === 'pending' ? 'Tertunda' :
                                                                             commission.status === 'paid' ? 'Dibayar' :
                                                                             commission.status === 'completed' ? 'Selesai' :
                                                                             commission.status === 'rejected' ? 'Ditolak' :
                                                                             commission.status === 'cancelled' ? 'Dibatalkan' :
                                                                             commission.status}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <button 
                                                                            className="btn btn-sm btn-info me-1" 
                                                                            onClick={() => viewCommissionDetails(commission)}
                                                                            title="Lihat Detail"
                                                                        >
                                                                            <i className="mdi mdi-eye"></i>
                                                                        </button>
                                                                        <button 
                                                                            className="btn btn-sm btn-secondary" 
                                                                            onClick={() => downloadCommissionPDF(commission)}
                                                                            title="Unduh Detail"
                                                                        >
                                                                            <i className="mdi mdi-download"></i>
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <i className="mdi mdi-currency-usd-off text-muted" style={{ fontSize: '3rem' }}></i>
                                                        <p className="mt-2">Anda belum mendapatkan komisi.</p>
                                                        <p className="text-muted">
                                                            Bagikan link referral Anda dari dashboard untuk mulai mendapatkan komisi!
                                                        </p>
                                                        <Link to="/user/dashboard" className="btn btn-primary mt-2">
                                                            Ke Dashboard
                                                        </Link>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {activeTab === 'withdrawals' && (
                                        <>
                                            <div className="table-responsive">
                                                {withdrawals && withdrawals.length > 0 ? (
                                                    <table className="table table-hover table-bordered">
                                                        <thead className="bg-light">
                                                            <tr>
                                                                <th>Tanggal</th>
                                                                <th>Jumlah</th>
                                                                <th>Bank</th>
                                                                <th>Status</th>
                                                                <th>Aksi</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {withdrawals.map((withdrawal, index) => (
                                                                <tr key={index}>
                                                                    <td>{moment(withdrawal.created_at).format('DD MMM YYYY')}</td>
                                                                    <td>{formatCurrency(withdrawal.amount || 0)}</td>
                                                                    <td>{withdrawal.bank_name}</td>
                                                                    <td>
                                                                        <span className={`badge ${getStatusBadgeClass(withdrawal.status)}`}>
                                                                            {withdrawal.status === 'pending' ? 'Tertunda' :
                                                                             withdrawal.status === 'approved' ? 'Disetujui' :
                                                                             withdrawal.status === 'completed' ? 'Selesai' :
                                                                             withdrawal.status === 'rejected' ? 'Ditolak' :
                                                                             withdrawal.status === 'cancelled' ? 'Dibatalkan' :
                                                                             withdrawal.status}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        {withdrawal.status === 'pending' && (
                                                                            <button 
                                                                                className="btn btn-sm btn-danger" 
                                                                                onClick={() => cancelWithdrawal(withdrawal.id)}
                                                                                title="Batalkan Penarikan"
                                                                            >
                                                                                <i className="mdi mdi-close"></i> Batalkan
                                                                            </button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <i className="mdi mdi-cash-remove text-muted" style={{ fontSize: '3rem' }}></i>
                                                        <p className="mt-2">Anda belum melakukan permintaan penarikan.</p>
                                                        <p className="text-muted">
                                                            Setelah memiliki saldo, Anda dapat melakukan penarikan dari dashboard.
                                                        </p>
                                                        <Link to="/user/dashboard" className="btn btn-primary mt-2">
                                                            Ke Dashboard
                                                        </Link>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Commission Details Modal */}
            {selectedCommission && (
                <div className={`modal fade ${showCommissionModal ? 'show' : ''}`} style={{display: showCommissionModal ? 'block' : 'none'}} tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Detail Komisi</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCommissionModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <h6 className="text-muted">Informasi Komisi</h6>
                                        <table className="table table-sm">
                                            <tbody>
                                                <tr>
                                                    <th>ID</th>
                                                    <td>{selectedCommission.id}</td>
                                                </tr>
                                                <tr>
                                                    <th>Jumlah</th>
                                                    <td>{formatCurrency(selectedCommission.amount)}</td>
                                                </tr>
                                                <tr>
                                                    <th>Status</th>
                                                    <td>
                                                        <span className={`badge ${getStatusBadgeClass(selectedCommission.status)}`}>
                                                            {selectedCommission.status === 'pending' ? 'Tertunda' :
                                                             selectedCommission.status === 'paid' ? 'Dibayar' :
                                                             selectedCommission.status === 'completed' ? 'Selesai' :
                                                             selectedCommission.status === 'rejected' ? 'Ditolak' :
                                                             selectedCommission.status === 'cancelled' ? 'Dibatalkan' :
                                                             selectedCommission.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th>Tanggal</th>
                                                    <td>{moment(selectedCommission.created_at).format('DD MMM YYYY HH:mm')}</td>
                                                </tr>
                                                {selectedCommission.notes && (
                                                    <tr>
                                                        <th>Catatan</th>
                                                        <td>{selectedCommission.notes}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-md-6">
                                        <h6 className="text-muted">Informasi Pemesanan</h6>
                                        {selectedCommission.booking ? (
                                            <table className="table table-sm">
                                                <tbody>
                                                    <tr>
                                                        <th>Kode Booking</th>
                                                        <td>{selectedCommission.booking.bookingCode || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Tanggal</th>
                                                        <td>{selectedCommission.booking.date ? moment(selectedCommission.booking.date).format('DD MMM YYYY') : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Atraksi</th>
                                                        <td>{selectedCommission.booking.attraction?.title || `Atraksi #${selectedCommission.booking.attraction_id}` || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Total Pembayaran</th>
                                                        <td>{formatCurrency(selectedCommission.booking.grandTotal || 0)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Tiket</th>
                                                        <td>
                                                            {selectedCommission.booking.tickets && selectedCommission.booking.tickets.length > 0 
                                                                ? `${selectedCommission.booking.tickets.reduce((sum, ticket) => sum + parseInt(ticket.quantity), 0)} tiket`
                                                                : selectedCommission.booking.ticketQuantity 
                                                                    ? `${selectedCommission.booking.ticketQuantity} tiket` 
                                                                    : '0 tiket'
                                                            }
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        ) : (
                                            <p className="text-muted">Tidak ada informasi pemesanan tersedia</p>
                                        )}
                                    </div>
                                </div>

                                {/* Ticket details if available */}
                                {selectedCommission.booking && selectedCommission.booking.tickets && selectedCommission.booking.tickets.length > 0 && (
                                    <div className="mt-3">
                                        <h6 className="text-muted">Detail Tiket</h6>
                                        <table className="table table-sm table-bordered">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Jenis Tiket</th>
                                                    <th>Harga</th>
                                                    <th>Jumlah</th>
                                                    <th>Subtotal</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedCommission.booking.tickets.map((ticket, index) => (
                                                    <tr key={index}>
                                                        <td>{ticket.name}</td>
                                                        <td>{formatCurrency(ticket.price)}</td>
                                                        <td>{ticket.quantity}</td>
                                                        <td>{formatCurrency(parseFloat(ticket.price) * ticket.quantity)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={() => downloadCommissionPDF(selectedCommission)}
                                >
                                    <i className="mdi mdi-download me-1"></i> Unduh Detail
                                </button>
                                <button 
                                    className="btn btn-primary" 
                                    onClick={() => setShowCommissionModal(false)}
                                >
                                    Tutup
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showCommissionModal && <div className="modal-backdrop fade show"></div>}

            {/* Withdrawal Modal */}
            <div className={`modal fade ${showWithdrawModal ? 'show' : ''}`} style={{display: showWithdrawModal ? 'block' : 'none'}} tabIndex="-1">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Tarik Dana</h5>
                            <button type="button" className="btn-close" onClick={() => setShowWithdrawModal(false)}></button>
                        </div>
                        <form onSubmit={handleWithdrawalSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <p>Saldo Tersedia: <strong>{formatCurrency(stats?.available_balance || 0)}</strong></p>
                                    <p className="text-muted small">Jumlah penarikan minimum: Rp 100.000</p>
                                </div>

                                {withdrawError && (
                                    <div className="alert alert-danger">{withdrawError}</div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">Jumlah Penarikan (Rp)</label>
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        value={withdrawalData.amount}
                                        onChange={(e) => setWithdrawalData({...withdrawalData, amount: e.target.value})}
                                        min="100000"
                                        max={stats?.available_balance || 0}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Nama Bank</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={withdrawalData.bank_name}
                                        onChange={(e) => setWithdrawalData({...withdrawalData, bank_name: e.target.value})}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Nomor Rekening</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={withdrawalData.account_number}
                                        onChange={(e) => setWithdrawalData({...withdrawalData, account_number: e.target.value})}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Nama Pemilik Rekening</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={withdrawalData.account_holder_name}
                                        onChange={(e) => setWithdrawalData({...withdrawalData, account_holder_name: e.target.value})}
                                        required
                                    />
                                </div>

                                <div className="alert alert-info small">
                                    <i className="mdi mdi-information-outline me-1"></i>
                                    Permintaan penarikan diproses dalam 1-3 hari kerja. Anda akan diberitahu ketika permintaan Anda diproses.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowWithdrawModal(false)}>Batal</button>
                                <button 
                                    type="submit" 
                                    className="btn btn-primary"
                                    disabled={withdrawLoading || !withdrawalData.amount || parseFloat(withdrawalData.amount) < 100000}
                                >
                                    {withdrawLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Memproses...
                                        </>
                                    ) : (
                                        'Kirim Permintaan Penarikan'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {showWithdrawModal && <div className="modal-backdrop fade show"></div>}

            <Footer />
        </>
    );
} 